import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useForm, FormProvider, Resolver } from 'react-hook-form'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { yupResolver } from '@hookform/resolvers/yup'
import { Add, Warning } from '@mui/icons-material'
import { CircularProgress } from '@mui/material'
import { RootState } from 'store'
import { errorMessageHandler, handleHookFormErrors } from 'utils/helpers'
import { blendSchema } from './validations'
import { Limitations } from './components/Limitations/Limitations'
import { BlendSuggestions } from './components/BlendSuggestions/BlendSuggestions'
import { BlendMixer } from './components/BlendMixer/BlendMixer'
import { BlendParameters } from '../../components/BlendParameters/BlendPrameters'
import {
  CrudAction,
  Entity,
  useFormChanges,
  usePageHeader,
  usePermissions,
} from '../../../../utils/hooks'
import { Button, Card } from '../../../../components'
import { TLimitation, TBlend, TAttribute } from '../../../../types'
import { useBlendingService, useProductsService } from 'services'
import {
  DetailsPageActionsContainer,
  DetailsPageContainer,
  DetailsPageMainContent,
  DetailsPageSection,
  DetailsPageActionsButtons,
  ButtonsContainer,
  LoadingContainer,
  BlendNameContainer,
} from '../../Blends.styled'
import {
  useAlertModal,
  useUnsavedChangesWarning,
} from '../../../../components/dialogs'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { BlendInstructions } from './components/BlendInstructions/BlendInstructions'

export const BlendDetails = () => {
  const { checkPermission } = usePermissions()
  const navigate = useNavigate()
  const { productId, blendId } = useParams()
  const location = useLocation()

  const {
    createBlends,
    updateBlends,
    getBlendById,
    calculateBlendSuggestions,
  } = useBlendingService()
  const { getProductById } = useProductsService()
  const queryClient = useQueryClient()

  const { data: blend } = useQuery({
    queryKey: ['blend_by_id', blendId],
    queryFn: () => getBlendById(blendId),
    enabled: blendId !== 'new',
  })
  const blendName = location.state?.blendName ?? blend?.result?.name

  const { data: product } = useQuery({
    queryKey: ['product_by_id', productId],
    queryFn: () => getProductById(productId),
    cacheTime: 0,
  })

  const [openHelp, setOpenHelp] = useState(false)

  usePageHeader({
    headerTitle: `${blendName}`,
    showGoBack: true,
    goBackUrl: '/blends',
    actionButton: (
      <Button
        variantType="primaryInverted"
        onClick={() => setOpenHelp(true)}
        sx={{
          border: '1px solid #ccc',
          borderRadius: '8px',
          width: 36,
          height: 36,
        }}
      >
        <InfoOutlinedIcon />
      </Button>
    ),
  })

  const isNew = blendId === 'new'
  const isEditMode = isNew || blend?.result?.accepted === false

  const validationRules = useSelector(
    (state: RootState) => state.validations.validationRules
  )

  const blendValidationSchema = useMemo(
    () => blendSchema(validationRules),
    [validationRules]
  )

  const formMethods = useForm<TBlend>({
    resolver: yupResolver(blendValidationSchema) as unknown as Resolver<TBlend>,
    mode: 'onChange',
    defaultValues: {
      name: blendName,
      attributes: isNew
        ? product?.result?.attributes
        : blend?.result?.attributes,
      company_id: isNew
        ? product?.result?.company_id
        : blend?.result?.company_id,
      suggestions: isNew ? [] : blend?.result?.suggestions || [],
      limitations: isNew ? [] : blend?.result?.limitations || [],
      mixer_settings: isNew ? [] : blend?.result?.mixer_settings || undefined,
      mixer_cost: isNew ? 0 : blend?.result?.mixer_cost || 0,
      suggested_cost: isNew ? 0 : blend?.result?.suggested_cost || 0,
      mixer_used: isNew ? false : blend?.result?.mixer_used || false,
    },
  })

  // To check if there are invalid input in the attributes input
  const {
    formState: { errors },
  } = formMethods
  const hasAttributeErrors = () => {
    return errors.attributes && Object.keys(errors.attributes).length > 0
  }

  useEffect(() => {
    if (blend?.result) {
      Object.keys(blend.result).forEach((key) => {
        formMethods.setValue(key as keyof TBlend, blend.result[key])
      })
    }
  }, [blend, formMethods.setValue])

  const createBlendDataMutation = useMutation(
    (newBlendData: Partial<TBlend>) => createBlends(newBlendData),
    {
      onSuccess: async () => {
        navigate('/blends')
        await queryClient.invalidateQueries('blends-draft')
      },
      onError: async (error: any) => {
        let errorMessage = "Can't update blend"
        if (error?.detail && error.detail === 'string') {
          errorMessage = error.detail
        } else if (error?.message) {
          errorMessage = error.message
        } else if (typeof error === 'string') {
          errorMessage = error
        }

        toast.error(errorMessage)
      },
    }
  )

  const updateBlendDataMutation = useMutation(
    (blendData: Partial<TBlend>) => updateBlends(blendData),
    {
      onSuccess: async () => {
        navigate('/blends')
        await queryClient.invalidateQueries('blends-draft')
      },
      onError: async (error: any) => {
        let errorMessage = "Can't update blend"
        if (error?.detail && error.detail === 'string') {
          errorMessage = error.detail
        } else if (error?.message) {
          errorMessage = error.message
        } else if (typeof error === 'string') {
          errorMessage = error
        }

        toast.error(errorMessage)
      },
    }
  )

  const { modal, showWarningModal, showInfoModal } = useAlertModal()

  const calculateBlendMutation = useMutation(
    ({
      limitations,
      attributes,
    }: {
      limitations: Partial<TLimitation>[]
      attributes: TAttribute[]
    }) => calculateBlendSuggestions(limitations, attributes),
    {
      onSuccess: async (data) => {
        if (!data?.result) return

        // BE sends bins with 0 value which are not needed for representation
        const filteredSuggestions = data.result.suggestions.map(
          (suggestion) => ({
            ...suggestion,
            suggestion_bins: suggestion.suggestion_bins.filter(
              (bin) => bin.value >= 1
            ),
          })
        )

        if (data.result?.status === 'warning') {
          toast.warning(data.result.detail)
        }

        const filteredMixers = data.result.mixers.map((mixer) => ({
          ...mixer,
          mixer_bins: mixer.mixer_bins.filter((bin) => bin.value >= 1),
        }))

        formMethods.setValue('suggestions', filteredSuggestions)
        formMethods.setValue('mixers', filteredMixers)
      },
      onError: async (error: { detail?: string }) => {
        showWarningModal({
          children: errorMessageHandler(
            error,
            'Can not calculate blend suggestions'
          ),
        })
      },
    }
  )

  const onSaveBlendAsDraft = formMethods.handleSubmit(
    (formData: TBlend) => {
      if (!product?.result) return
      setSuggestionsChanged(false)
      setMixersChanged(false)
      const normalizedAttributes = normalizeAttributes(formData.attributes)
      if (isNew) {
        createBlendDataMutation.mutate({
          name: blendName,
          product_id: product.result.id,
          attributes: normalizedAttributes,
          limitations: formData.limitations,
          company_id: product?.result?.company_id,
          mixers: formData.mixers,
          suggestions: formData.suggestions,
          calculation_required: false,
          mixer_used:
            !!formData.mixers?.find((mixer) => !!mixer.selected)?.id || false,
          accepted: false,
        })
      } else {
        updateBlendDataMutation.mutate({
          id: blend?.result.id,
          ...formData,
          attributes: normalizedAttributes,
          limitations: formData.limitations,
          mixer_used:
            !!formData.mixers?.find((mixer) => !!mixer.selected)?.id || false,
          accepted: false,
        })
      }
    },
    (errors) => {
      handleHookFormErrors(errors)
    }
  )

  const onAcceptSelectedSuggestion = formMethods.handleSubmit(
    (formData: TBlend) => {
      if (!product?.result) return

      if (formData.suggestions?.length < 1) {
        toast.error('Please add suggestions')
        return
      }
      setSuggestionsChanged(false)
      setMixersChanged(false)
      const normalizedAttributes = normalizeAttributes(formData.attributes)
      if (isNew) {
        createBlendDataMutation.mutate({
          name: blendName,
          product_id: product.result.id,
          attributes: normalizedAttributes,
          limitations: formData.limitations,
          company_id: product?.result?.company_id,
          mixers: formData.mixers,
          suggestions: formData.suggestions,
          calculation_required: false,
          accepted: true,
          mixer_used:
            !!formData.mixers?.find((mixer) => !!mixer.selected)?.id || false,
        })
      } else {
        updateBlendDataMutation.mutate({
          id: blend?.result.id,
          ...formData,
          attributes: normalizedAttributes,
          mixer_used:
            !!formData.mixers?.find((mixer) => !!mixer.selected)?.id || false,
          accepted: true,
        })
      }
    },
    (errors) => {
      handleHookFormErrors(errors)
    }
  )

  const { suggestions, mixers, accepted, attributes, limitations } =
    formMethods.watch()

  const attributesChanged =
    formMethods.formState.dirtyFields?.attributes?.length > 0
  const limitationsChanged =
    formMethods.formState.dirtyFields?.limitations?.length > 0

  const { needsRecalculation, resetChanges } = useFormChanges({
    watchedValues: { attributes: attributes, limitations: limitations },
    initialValues: {
      attributes: blend?.result?.attributes || [],
      limitations: blend?.result?.limitations || [],
    },
    areFieldsDirty: attributesChanged || limitationsChanged,
  })

  const [suggestionsChanged, setSuggestionsChanged] = useState(false)
  const [mixersChanged, setMixersChanged] = useState(false)

  const { confirmModal } = useUnsavedChangesWarning(
    needsRecalculation || suggestionsChanged || mixersChanged
  )

  const onCalculateBlendSuggestions = () => {
    const formData = formMethods.getValues()
    const normalizedAttributes = normalizeAttributes(formData.attributes)

    if (
      normalizedAttributes.every(
        (attribute) => !attribute.range_min && !attribute.range_max
      )
    ) {
      showInfoModal({
        children: 'At least one blend spec’s parameter range must be specified',
      })
      return
    }

    calculateBlendMutation.mutate({
      limitations: formData.limitations,
      attributes: normalizedAttributes,
    })

    resetChanges({
      attributes: formData.attributes,
      limitations: formData.limitations,
    })
  }

  useEffect(() => {
    if (needsRecalculation) {
      toast.warning(
        'Parameters have changed, please calculate blend suggestions'
      )
    }
  }, [needsRecalculation])

  const normalizeAttributes = (attributes?: TAttribute[]): TAttribute[] => {
    if (!attributes) return []

    return attributes.map((attr) => ({
      ...attr,
      range_min: attr.range_min ?? 0,
      range_max: attr.range_max ?? 0,
      range_avg: attr.range_avg ?? 0,
    }))
  }

  const mixerUsed = mixers?.find((mixer) => !!mixer.selected)?.id || false

  return (
    <FormProvider {...formMethods}>
      <DetailsPageContainer>
        <DetailsPageMainContent>
          <DetailsPageSection>
            <BlendNameContainer>
              Product: {product?.result?.name || ''}
            </BlendNameContainer>
            <BlendParameters
              attributes={
                isNew ? product?.result?.attributes : blend?.result?.attributes
              }
              mode={isEditMode ? 'edit' : 'disabled'}
            />
            <Limitations />
          </DetailsPageSection>
          <DetailsPageSection>
            {suggestions?.length > 0 && (
              <BlendSuggestions setFieldsChanged={setSuggestionsChanged} />
            )}
            {mixers?.length > 0 && (
              <BlendMixer setFieldsChanged={setMixersChanged} />
            )}
          </DetailsPageSection>
        </DetailsPageMainContent>
        <Card>
          <DetailsPageActionsContainer>
            <DetailsPageActionsButtons>
              <Button
                disabled={
                  calculateBlendMutation.isLoading ||
                  accepted ||
                  hasAttributeErrors()
                }
                onClick={() => {
                  // Trigger validation before proceeding
                  formMethods.trigger().then((isValid) => {
                    if (isValid) {
                      onCalculateBlendSuggestions()
                    }
                  })
                }}
                sx={{
                  boxShadow: needsRecalculation
                    ? `0px 0px 8px #D13E43`
                    : 'unset',
                }}
                startIcon={needsRecalculation && <Warning />}
              >
                {calculateBlendMutation.isLoading ? (
                  <CircularProgress size={24} color="primary" />
                ) : (
                  'Calculate Blend Suggestion'
                )}
              </Button>
              {createBlendDataMutation.isLoading ||
              updateBlendDataMutation.isLoading ? (
                <LoadingContainer>
                  <CircularProgress size={30} color="primary" />
                </LoadingContainer>
              ) : (
                <ButtonsContainer>
                  <Button
                    disabled={
                      accepted ||
                      needsRecalculation ||
                      hasAttributeErrors() ||
                      !checkPermission(Entity.BLENDING, CrudAction.UPDATE) ||
                      calculateBlendMutation.isLoading
                    }
                    variantType="secondary"
                    onClick={() => {
                      formMethods.trigger().then((isValid) => {
                        if (isValid) {
                          void onSaveBlendAsDraft()
                        }
                      })
                    }}
                  >
                    Save Draft
                  </Button>
                  <Button
                    disabled={
                      accepted ||
                      needsRecalculation ||
                      hasAttributeErrors() ||
                      !checkPermission(Entity.BLENDING, 'accept') ||
                      calculateBlendMutation.isLoading ||
                      !!(mixersChanged && mixerUsed)
                    }
                    startIcon={<Add />}
                    onClick={() => {
                      formMethods.trigger().then((isValid) => {
                        if (isValid) {
                          void onAcceptSelectedSuggestion()
                        }
                      })
                    }}
                  >
                    Accept Selected Suggestion
                  </Button>
                </ButtonsContainer>
              )}
            </DetailsPageActionsButtons>
          </DetailsPageActionsContainer>
        </Card>
        {confirmModal}
        {modal}
      </DetailsPageContainer>
      <BlendInstructions open={openHelp} onClose={() => setOpenHelp(false)} />
    </FormProvider>
  )
}
